/**
 * Punto único de acceso a datos. Hoy devuelve los mocks; al integrar en connectacleaner-front
 * se reemplaza cada método por la llamada real (ver HANDOFF.md § "Contratos de datos").
 *
 * REGLAS (no negociables en la integración):
 *  - Nada de esta capa envía mensajes, cobra ni modifica agendas reales sin confirmación humana.
 *  - Al reprogramar se envía `expectedVisitState`; si el servidor responde 409, la UI muestra
 *    “La visita cambió” con el estado actual y NO reintenta sola.
 *  - `routeReadiness` solo dice si las ubicaciones están completas; la llegada se confirma
 *    únicamente con el registro de inicio (startAt).
 */
import { gateway } from '../../../gateway';
import { DateTime } from 'luxon';
import { fetchCallHistory } from '../../../components/virtual-receptionist/calls/actions/callHistory.actions';
import type { RescheduleRequest, SmartSuggestion, SpaceRequest, Visit } from './types';
import { BLOCKS, CLIENTS, CLOSED_DAYS, INVOICES, NOW, QUEUE, TEAMS, TODAY, VISITS } from './mock';

export interface AgendaApi {
  /** GET /jobs/events/self?date=…  (+ asignaciones con status y startAt) */
  listVisits(fromDate: string, toDate: string): Promise<Visit[]>;
  /** POST /jobs/smart-scheduling — sugerencias con routeReadiness (PR #1009) */
  smartScheduling(req: SpaceRequest, date: string): Promise<SmartSuggestion[]>;
  /** PATCH /jobs/event/:uid/workforce (o endpoint de #1009) con expectedVisitState */
  reschedule(req: RescheduleRequest): Promise<{ ok: true } | { ok: false; status: 409; current: Visit }>;
}

export const mockSnapshot = {
  today: TODAY, now: NOW, teams: TEAMS, visits: VISITS, blocks: BLOCKS, clients: CLIENTS,
  closedDays: CLOSED_DAYS, queue: QUEUE, invoices: INVOICES,
};

/** The existing backend treats endDate as inclusive. Use the last millisecond
 * of the company's civil day so a visit starting at next midnight is excluded. */
export function companyDayRange(now: DateTime, zone: string): { startDate: string; endDate: string } {
  const local = now.setZone(zone);
  if (!local.isValid || !zone) throw new Error('Zona horaria de empresa no disponible');
  const start = local.startOf('day');
  return {
    startDate: start.toUTC().toISO()!,
    endDate: start.plus({ days: 1 }).minus({ milliseconds: 1 }).toUTC().toISO()!,
  };
}

export function companySuggestionDate(now: DateTime, zone: string, when: SpaceRequest['when']): string {
  const local = now.setZone(zone);
  if (!local.isValid || !zone) throw new Error('Zona horaria de empresa no disponible');
  return local.plus({ days: when === 'manana' ? 1 : 0 }).toISODate()!;
}

/** Read-only data already provided by connectacleaner-back. Its team/capacity model is
 * not the same as this prototype's Team model, so the prototype calendar stays visibly
 * synthetic until the company team/capacity API exists. Never merge these two datasets. */
export async function countRealVisits(startDate: string, endDate: string): Promise<number> {
  const response = await gateway.get('/jobs/events/self', {
    params: { startDate, endDate, page: 1, pageSize: 1 },
  });
  const count = response.data?.data?.totalItems;
  if (typeof count !== 'number' || !Number.isSafeInteger(count) || count < 0) throw new Error('Respuesta de visitas inválida');
  return count;
}

export async function countPendingInvoices(): Promise<number> {
  const response = await gateway.get('/invoices/list', { params: { page: 1, pagesize: 1, paymentStatus: 'pending' } });
  const count = response.data?.data?.totalItems;
  if (typeof count !== 'number' || !Number.isSafeInteger(count) || count < 0) throw new Error('Respuesta de facturas inválida');
  return count;
}

export async function getRecentCallSummary(): Promise<{ recent: number; answered: number; missed: number }> {
  const response = await fetchCallHistory({ limit: 100, pageParam: 0 });
  if (!Array.isArray(response.data)) throw new Error('Respuesta de llamadas inválida');
  return {
    recent: response.data.length,
    answered: response.data.filter(call => call.direction === 'inbound' && call.status === 'completed').length,
    missed: response.data.filter(call => call.direction === 'inbound' && ['no-answer', 'busy'].includes(call.status)).length,
  };
}

export interface BackendSuggestion {
  rank: number;
  score: number;
  scoreMeaning: 'RANKING_ONLY';
  feasibilityStatus: 'UNVERIFIED';
  routeReadiness: { status: string; missingInputs: string[]; itineraryFingerprint?: string | null };
  unverifiedConstraints: string[];
  travelDistanceMiles: number | null;
  schedule: { startDate: string; duration: number };
  workforce: { employees: unknown[]; contractors: unknown[]; supervisors: unknown[] };
}

/** Existing endpoint needs a persisted company job. A new caller/request has no jobId,
 * so the three-step prototype cannot be promoted to a real booking flow yet. */
export async function getRealSuggestions(jobId: number, date: string, durationH: number, days = 1): Promise<BackendSuggestion[]> {
  if (!Number.isSafeInteger(jobId) || jobId < 1) throw new Error('Se requiere un job existente');
  const response = await gateway.post('/jobs/smart-scheduling', {
    jobId, startDate: date, duration: Math.round(durationH * 60), days, limit: 5,
  });
  if (!Array.isArray(response.data?.data)) throw new Error('Respuesta de sugerencias inválida');
  return response.data.data;
}

// TODO(API): A move needs a preview plus expectedStartDate, expectedEndDate and
// expectedEventUid on PATCH /jobs/event/:eventUid. The prototype's
// expectedVisitState object is not accepted by #1009 as a concurrency token.
// Do not issue any write until this is mapped and the human confirms the change.
